import { useState } from "react";
import { Info, TriangleAlert, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "../ui/alert";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  announcementKey,
  localizedMessage,
  type PublicAppStatus,
} from "../../lib/app-status-policy";

type Props = { announcement: PublicAppStatus["announcement"] };
export function AnnouncementBanner({ announcement }: Props) {
  const { i18n } = useTranslation();
  const text = localizedMessage(announcement.message, i18n.resolvedLanguage || i18n.language);
  if (!announcement.enabled || !text) return null;
  const storageKey = announcementKey(announcement.message);
  return (
    <AnnouncementMessage
      key={storageKey}
      storageKey={storageKey}
      text={text}
      tone={announcement.tone}
    />
  );
}
function AnnouncementMessage({
  storageKey,
  text,
  tone,
}: {
  storageKey: string;
  text: string;
  tone: "info" | "warning";
}) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "dismissed";
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  const Icon = tone === "warning" ? TriangleAlert : Info;
  return (
    <Alert
      role={tone === "warning" ? "alert" : "status"}
      className={cn(
        "shrink-0 rounded-none border-x-0 border-t-0",
        tone === "warning" && "[&>svg]:text-quality-warning",
      )}
    >
      <Icon />
      <AlertDescription className="flex min-w-0 flex-row items-start justify-between gap-3 text-foreground">
        <p className="min-w-0 self-center break-words whitespace-pre-wrap">{text}</p>
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-label={t("common.close")}
          onClick={() => {
            setDismissed(true);
            try {
              localStorage.setItem(storageKey, "dismissed");
            } catch {
              /* Dismiss for this mount when storage is unavailable. */
            }
          }}
        >
          <X />
        </Button>
      </AlertDescription>
    </Alert>
  );
}
